import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Building2, Star, Power, Pencil, Trash2 } from "lucide-react";
import AgencyFormDialog from "@/components/AgencyFormDialog";
import { useAgency } from "@/context/AgencyContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

export default function AgenciesPage() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { refresh: refreshAgencyContext, activeId } = useAgency();

  const load = async () => {
    const { data } = await api.get("/rep-agencies");
    setList(data);
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = async () => {
    await load();
    await refreshAgencyContext();
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/rep-agencies/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Agency deactivated" : "Agency activated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Update failed"));
    }
  };

  const remove = async (a) => {
    if (a.is_default) {
      toast.error("Cannot delete the default agency");
      return;
    }
    if (!window.confirm(`Delete ${a.name}? This removes all of its manufacturer assignments.`)) {
      return;
    }
    try {
      await api.delete(`/rep-agencies/${a.id}`);
      toast.success("Agency deleted");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data">
            Multi-tenant
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Rep Agencies
          </h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Each agency has its own manufacturer portfolio, territories, and
            pricing-contact emails. Distributors search inside one agency's
            scope at a time.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          className="bg-slate-900 hover:bg-slate-800 rounded-sm"
          data-testid="add-agency-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> New rep agency
        </Button>
      </div>

      {list === null ? (
        <div className="byrd-progress" />
      ) : list.length === 0 ? (
        <EmptyState onAdd={() => setOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((a) => (
            <div
              key={a.id}
              data-testid={`agency-card-${a.id}`}
              className={`group bg-white border rounded-sm p-5 transition-colors ${
                activeId === a.id ? "border-amber-500" : "border-border hover:border-slate-400"
              }`}
            >
              <Link to={`/agencies/${a.id}`} className="block">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 border border-border rounded-sm flex items-center justify-center bg-slate-50 overflow-hidden shrink-0">
                    {a.logo_url ? (
                      <img src={a.logo_url} alt={a.name} className="w-full h-full object-contain p-1" />
                    ) : (
                      <Building2 className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="font-display font-semibold text-slate-900 truncate group-hover:text-amber-600">
                        {a.name}
                      </div>
                      {a.is_default && (
                        <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 shrink-0" />
                      )}
                    </div>
                    {a.website ? (
                      <div className="mt-0.5 text-xs text-muted-foreground truncate">
                        {a.website}
                      </div>
                    ) : (
                      <div className="mt-0.5 text-xs text-muted-foreground">No website</div>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs font-mono-data">
                  <span className="text-muted-foreground uppercase tracking-wider">
                    {a.line_count} {a.line_count === 1 ? "line" : "lines"}
                    {" · "}
                    {(a.pricing_emails?.length || 0)} {(a.pricing_emails?.length || 0) === 1 ? "email" : "emails"}
                  </span>
                  <span
                    className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${
                      a.active !== false
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-rose-300 bg-rose-50 text-rose-700"
                    }`}
                  >
                    {a.active !== false ? "Active" : "Inactive"}
                  </span>
                </div>
              </Link>

              <div className="mt-3 flex items-center gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-7 px-2 text-[11px] flex-1"
                  onClick={() => {
                    setEditing(a);
                    setOpen(true);
                  }}
                  data-testid={`agency-edit-btn-${a.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-7 px-2 text-[11px]"
                  onClick={() => toggleActive(a)}
                  data-testid={`agency-toggle-btn-${a.id}`}
                  title={a.active !== false ? "Deactivate" : "Activate"}
                >
                  <Power className="w-3 h-3" />
                </Button>
                {!a.is_default && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-7 px-2 text-[11px] text-rose-700 hover:text-rose-800"
                    onClick={() => remove(a)}
                    data-testid={`agency-delete-btn-${a.id}`}
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AgencyFormDialog
        open={open}
        onOpenChange={setOpen}
        mode={editing ? "edit" : "create"}
        agency={editing}
        onSaved={onSaved}
      />
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="border border-dashed border-border rounded-sm bg-white p-12 text-center" data-testid="agencies-empty">
      <div className="w-12 h-12 mx-auto bg-slate-900 text-white flex items-center justify-center rounded-sm">
        <Building2 className="w-5 h-5" />
      </div>
      <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
        No agencies yet
      </h3>
      <p className="mt-1 text-sm text-muted-foreground max-w-md mx-auto">
        Create a rep agency to start scoping its manufacturer portfolio.
      </p>
      <Button onClick={onAdd} className="mt-4 bg-slate-900 hover:bg-slate-800 rounded-sm">
        <Plus className="w-4 h-4 mr-2" /> New rep agency
      </Button>
    </div>
  );
}
